import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebase-config';
import { httpsCallable, getFunctions } from 'firebase/functions';

const functions = getFunctions();
const trackUserActivity = httpsCallable(functions, 'trackUserActivity');
const batchTrackActivity = httpsCallable(functions, 'batchTrackActivity');

// Activity tracking hook
export const useActivityTracker = () => {
  const location = useLocation();
  const user = auth.currentUser;
  const [sessionId] = useState(() => Math.random().toString(36).substr(2, 9));
  const [activityQueue, setActivityQueue] = useState([]);

  // Track page views
  useEffect(() => {
    const trackPageView = async (path) => {
      try {
        // Mock tracking - store locally instead of Firebase Functions
        const activityData = {
          userId: user?.uid || 'anonymous',
          action: 'page_view',
          page: path,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer
        };
        
        // Store in localStorage for now
        const activities = JSON.parse(localStorage.getItem('user_activities') || '[]');
        activities.push(activityData);
        localStorage.setItem('user_activities', JSON.stringify(activities.slice(-100))); // Keep last 100
        
        console.log('Page view tracked locally:', activityData);
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    };

    trackPageView(location.pathname);
  }, [location, sessionId, user]);

  // Track interactions
  const trackInteraction = async (action, element, metadata = {}) => {
    try {
      const activityData = {
        action,
        page: location.pathname,
        element,
        metadata: {
          ...metadata,
          sessionId,
          timestamp: Date.now()
        },
        timestamp: Date.now()
      };

      // Add to queue for batch processing
      setActivityQueue(prev => [...prev, activityData]);

      // Also send immediately for important actions
      if (['form_submit', 'download', 'purchase'].includes(action)) {
        await trackUserActivity(activityData);
      }
    } catch (error) {
      console.error('Error tracking interaction:', error);
    }
  };

  // Batch send queued activities every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      if (activityQueue.length > 0) {
        try {
          // Store batch activities locally
          const activities = JSON.parse(localStorage.getItem('user_activities') || '[]');
          const batchData = activityQueue.map(activity => ({
            ...activity,
            sessionId,
            userId: user?.uid || 'anonymous',
            timestamp: new Date().toISOString()
          }));
          
          activities.push(...batchData);
          localStorage.setItem('user_activities', JSON.stringify(activities.slice(-100)));
          
          console.log('Batch activities tracked locally:', batchData);
          setActivityQueue([]);
        } catch (error) {
          console.error('Error batch tracking:', error);
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [activityQueue, sessionId, user]);

  // Send remaining activities before page unload
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (activityQueue.length > 0) {
        try {
          // Store remaining activities locally
          const activities = JSON.parse(localStorage.getItem('user_activities') || '[]');
          const remainingData = activityQueue.map(activity => ({
            ...activity,
            sessionId,
            userId: user?.uid || 'anonymous',
            timestamp: new Date().toISOString()
          }));
          
          activities.push(...remainingData);
          localStorage.setItem('user_activities', JSON.stringify(activities.slice(-100)));
          
          console.log('Remaining activities tracked locally:', remainingData);
          await batchTrackActivity({ activities: activityQueue });
        } catch (error) {
          console.error('Error sending final activities:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activityQueue]);

  return { trackInteraction };
};

// Activity Tracker Component
export default function ActivityTracker({ children }) {
  const { trackInteraction } = useActivityTracker();

  useEffect(() => {
    // Track clicks on important elements
    const handleClick = (event) => {
      const element = event.target;
      const tagName = element.tagName.toLowerCase();
      const className = element.className;
      const id = element.id;
      const text = element.textContent?.slice(0, 50) || '';

      // Track button clicks
      if (tagName === 'button' || element.type === 'button') {
        trackInteraction('click', 'button', {
          buttonText: text,
          className,
          id
        });
      }

      // Track link clicks
      if (tagName === 'a') {
        trackInteraction('click', 'link', {
          href: element.href,
          linkText: text,
          className,
          id
        });
      }

      // Track form submissions
      if (tagName === 'input' && element.type === 'submit') {
        trackInteraction('form_submit', 'form', {
          formName: element.form?.name || element.form?.id || 'unnamed',
          className,
          id
        });
      }

      // Track specific HoppHomes elements
      if (className?.includes('pricing') || id?.includes('pricing')) {
        trackInteraction('click', 'pricing_element', {
          elementType: 'pricing',
          text,
          className,
          id
        });
      }

      if (className?.includes('calculator') || id?.includes('calculator')) {
        trackInteraction('click', 'calculator', {
          elementType: 'calculator',
          text,
          className,
          id
        });
      }

      if (className?.includes('affiliate') || id?.includes('affiliate')) {
        trackInteraction('click', 'affiliate_element', {
          elementType: 'affiliate',
          text,
          className,
          id
        });
      }

      if (className?.includes('gallery') || id?.includes('gallery')) {
        trackInteraction('click', 'gallery_element', {
          elementType: 'gallery',
          text,
          className,
          id
        });
      }

      if (className?.includes('contact') || id?.includes('contact')) {
        trackInteraction('click', 'contact_element', {
          elementType: 'contact',
          text,
          className,
          id
        });
      }
    };

    // Track form submissions
    const handleSubmit = (event) => {
      const form = event.target;
      trackInteraction('form_submit', 'form', {
        formName: form.name || form.id || 'unnamed',
        formAction: form.action,
        formMethod: form.method
      });
    };

    // Track downloads
    const handleDownload = (event) => {
      const element = event.target;
      if (element.tagName.toLowerCase() === 'a' && element.download) {
        trackInteraction('download', 'file', {
          fileName: element.download,
          fileUrl: element.href,
          fileType: element.href.split('.').pop()
        });
      }
    };

    // Track video plays
    const handleVideoPlay = (event) => {
      const video = event.target;
      trackInteraction('video_play', 'video', {
        videoSrc: video.src,
        videoDuration: video.duration,
        currentTime: video.currentTime
      });
    };

    // Track scroll depth
    let maxScrollDepth = 0;
    const handleScroll = () => {
      const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      if (scrollDepth > maxScrollDepth && scrollDepth % 25 === 0) {
        maxScrollDepth = scrollDepth;
        trackInteraction('scroll', 'page_scroll', {
          scrollDepth: scrollDepth,
          scrollY: window.scrollY
        });
      }
    };

    // Add event listeners
    document.addEventListener('click', handleClick);
    document.addEventListener('submit', handleSubmit);
    document.addEventListener('click', handleDownload);
    document.addEventListener('play', handleVideoPlay, true);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('submit', handleSubmit);
      document.removeEventListener('click', handleDownload);
      document.removeEventListener('play', handleVideoPlay, true);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [trackInteraction]);

  return <>{children}</>;
}
